import React, { useState, useRef } from "react";

const CollapsibleCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
    const [open, setOpen] = useState(false);
    const idRef = useRef(`collapsible-${Math.random().toString(36).substr(2, 9)}`);
    const toggle = () => setOpen((s) => !s);

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
        }
    };

    return (
        <div className="card">
            <div
                className="card-header bg-white fw-bold py-3 text-muted d-flex align-items-center"
                role="button"
                tabIndex={0}
                onClick={toggle}
                onKeyDown={onKeyDown}
                aria-expanded={open}
                aria-controls={idRef.current}
                style={{ cursor: "pointer" }}
            >
                <div>{title}</div>
                <button
                    type="button"
                    className="btn btn-link ms-auto p-0"
                    aria-expanded={open}
                    aria-label={open ? "Close" : "Open"}
                    onClick={(e) => {
                        e.stopPropagation();
                        toggle();
                    }}
                >
                    {!open ? <span className="fs-4 bi bi-plus" /> : <span className="fs-4 bi bi-x" />}
                </button>
            </div>
            {open && (
                <div id={idRef.current} className="card-body">
                    {children}
                </div>
            )}
        </div>
    );
};

const FAQ: React.FC = () => {
    return (
        <main className="container">
            <div className="row text-center">
                <div className="col-12">
                    <img src="/brand.png" alt="brand" className="brand" />
                </div>
            </div>

            <div className="row text-center">
                <div className="col-12">
                    <h2>Frequently Asked Questions</h2>
                </div>
                <div className="col-12">
                    <p>Travel and Accommodation Reimbursement for Seminar Participants</p>
                </div>
            </div>

            <div className="row mb-3">
                <div className="col-12 mb-3">
                    <h4>General & Access</h4>
                </div>

                <div className="col-12 mb-2">
                    <CollapsibleCard title="Who can use the Travel Cost Tool?">
                        The tool is generally accessible to <strong>any visitor</strong> for calculating eligible travel costs. No login required. However, certain features (viewing your own claims, saving bank details) require authentication as a <strong>guest user</strong>.
                    </CollapsibleCard>
                </div>

                <div className="col-12 mb-2">
                    <CollapsibleCard title="How does the login process work?">
                        <p className="mb-2">Login is a two-step process:</p>
                        <ul>
                            <li><strong>Step 1:</strong> Enter your email address and confirm. A one-time verification code will be sent to you.</li>
                            <li><strong>Step 2:</strong> Enter the confirmation code. After successful verification you are logged in automatically.</li>
                        </ul>
                        <div className="alert alert-primary mb-0">
                            Please use the same email address you provided when originally saving your claim.
                        </div>
                    </CollapsibleCard>
                </div>

                <div className="col-12 mb-2">
                    <CollapsibleCard title="Where can I find the login page?">
                        <p className="mb-0">You can log in at <a href="https://unitcosts.era.law/login">https://unitcosts.era.law/login</a></p>
                    </CollapsibleCard>
                </div>

                <div className="col-12 my-3">
                    <h4>Submitting a Claim</h4>
                </div>

                <div className="col-12 mb-2">
                    <CollapsibleCard title="When can I submit my travel cost claim?">
                        <p className="mb-0">
                            The form can be filled in and <strong>saved before the seminar takes place</strong>, but it can only be <strong>submitted once the seminar has ended</strong>. To do so, open your claim via the "Details" button and click "Request contribution".
                        </p>
                    </CollapsibleCard>
                </div>

                <div className="col-12 mb-2">
                    <CollapsibleCard title="What information is needed to calculate the contribution?">
                        <p className="mb-2">The following details are required:</p>
                        <ul className="mb-2">
                            <li><strong>Employer's address</strong> (starting point of the journey)</li>
                            <li><strong>Type of travel</strong> (e.g. plane, combined)</li>
                            <li><strong>Number of nights spent</strong></li>
                        </ul>
                        <p className="mb-0">Distances are calculated automatically using the European Commission's geographic information system.</p>
                    </CollapsibleCard>
                </div>

                <div className="col-12 mb-2">
                    <CollapsibleCard title="What if my organisation does not appear in the suggestion list?">
                        If the full address cannot be found, you can select <strong>just the city</strong> of your employer from the list. The system will then prompt you in a separate input screen to provide the missing details (organisation name, street, postal code). Fields that need to be completed are <strong>highlighted in yellow</strong>.
                    </CollapsibleCard>
                </div>

                <div className="col-12 my-3">
                    <h4>Bank Details & Payments</h4>
                </div>

                <div className="col-12 mb-2">
                    <CollapsibleCard title="When and how do I provide my bank details?">
                        <p className="mb-2">Bank details are requested when you submit your reimbursement claim. You will need to provide: IBAN, BIC, bank name, bank address, as well as the account holder's name and address. The IBAN you enter is automatically validated for correctness.</p>
                        <div className="alert alert-primary">
                            The bank details step is skipped if the IBAN provided already confirms that a SEPA transfer is supported.
                        </div>
                    </CollapsibleCard>
                </div>

                <div className="col-12 mb-2">
                    <CollapsibleCard title="Does the account holder name need to be entered exactly?">
                        Yes. Since <strong>9 October 2025</strong>, banks verify whether the recipient name matches the IBAN provided. You must therefore enter the <strong>exact account holder name</strong> as it appears on the account, to ensure the transfer is processed successfully.
                    </CollapsibleCard>
                </div>

                <div className="col-12 my-3">
                    <h4>Supporting Documents</h4>
                </div>

                <div className="col-12 mb-2">
                    <CollapsibleCard title="What supporting documents do I need to upload?">
                        The Academy of European Law requires documents that make the information provided verifiable — in particular the type of travel. If you stayed in a hotel or other type of accommodation, you must upload both a <strong>travel receipt and an accommodation receipt</strong>.
                    </CollapsibleCard>
                </div>

                <div className="col-12 mb-2">
                    <CollapsibleCard title="What file formats and sizes are accepted?">
                        <p className="mb-2">Accepted formats:</p>
                        <ul className="mb-2">
                            <li>Image files: <strong>.jpg, .jpeg, .png, .gif, .svg</strong></li>
                            <li>PDF documents: <strong>.pdf</strong></li>
                        </ul>
                        <p className="mb-0">Maximum file size: <strong>50 MB per file</strong> — up to <strong>10 files</strong> per claim.</p>
                    </CollapsibleCard>
                </div>

                <div className="col-12 mb-2">
                    <CollapsibleCard title="What happens if a document is rejected?">
                        <p className="mb-2">If an invalid file is uploaded, one of the following error messages will appear:</p>
                        <div className="alert alert-warning">
                            The uploaded file type is not supported. Please upload an image or a PDF file." — The file format is not permitted
                        </div>
                        <div className="alert alert-warning">
                            The uploaded files exceed the maximum size allowed. The maximum size allowed per file is 50 MB. A maximum of 10 files may be uploaded per claim." — A file is too large or too many files have been uploaded.
                        </div>
                    </CollapsibleCard>
                </div>

                <div className="col-12 mb-2">
                    <CollapsibleCard title="How do I assign a category to an uploaded document?">
                        After uploading, each file appears in the list with a <strong>category dropdown menu</strong>. Typically the available categories are "Receipt: Travel" and "Receipt: Accommodation".
                    </CollapsibleCard>
                </div>

                <div className="col-12 my-3">
                    <h4>Status & Overview</h4>
                </div>

                <div className="col-12 mb-2">
                    <CollapsibleCard title="How can I check the status of my claim?">
                        As a logged-in guest user, you can view all your saved claims along with their current <strong>processing status</strong> in the overview. The detail view also shows the calculated distance, travel type, number of nights, and the total reimbursement amount.
                    </CollapsibleCard>
                </div>

                <div className="col-12 mb-2">
                    <CollapsibleCard title="When will I receive the link to fill in the form?">
                        You will receive the access link <strong>before the seminar</strong> from the ERA's Event Coordinator.
                    </CollapsibleCard>
                </div>

            </div>

        </main>
    );
};

export default FAQ;